import { useState } from 'react';
import { FixedNav } from '@/ui/FixedNav';
import { useRatings } from '@/api/ratings';
import { VueGlobalePanel } from './VueGlobalePanel';
import { ConsistencyAlerts } from './ConsistencyAlerts';
import { EloHistoryChart, type CadenceTab } from './EloHistoryChart';
import { ProgressionStats } from './ProgressionStats';
import { FideTab } from './FideTab';
import { LichessTab } from './LichessTab';
import { ChesscomTab } from './ChesscomTab';
import { PerformancePage } from './PerformancePage';
import type { StatsTab } from './types';

/**
 * StatsPage — route `/stats` (T3 → T6 Salve P1 Stats Hub).
 *
 * 4 onglets : Vue globale (default) | FIDE | Lichess | chess.com
 *  - Vue globale : `<RatingsSummaryCard>` + `<ConsistencyAlerts>` + courbe ELO (T3 + T5)
 *  - FIDE : `<FideTab>` 3 cards + `<FideRatingInputModal>` + `<FideHistoryTable>` (T6)
 *  - Lichess : `<LichessTab>` 6 perfs + lien profil (T6)
 *  - chess.com : `<ChesscomTab>` 4 cadences (T6)
 */

const TABS: Array<{ id: StatsTab; label: string; subtitle: string }> = [
  { id: 'overview', label: 'Vue globale', subtitle: 'FIDE · Lichess · chess.com' },
  { id: 'fide', label: 'FIDE', subtitle: 'Officiel' },
  { id: 'lichess', label: 'Lichess', subtitle: 'Entraînement' },
  { id: 'chesscom', label: 'chess.com', subtitle: 'Volume' },
  { id: 'performance', label: 'Performance', subtitle: 'Cross-cadence' },
];

export function StatsPage(): JSX.Element {
  const [tab, setTab] = useState<StatsTab>('overview');
  const [overviewCadence, setOverviewCadence] = useState<CadenceTab>('classique');
  const ratingsQ = useRatings();

  return (
    <>
      <FixedNav />
      <main className="max-w-6xl mx-auto px-4 py-6">
        <header className="mb-6">
          <h1 className="text-display-2 text-paper-50">Stats</h1>
          <p className="text-ink-300 italic mt-1">
            Tous tes ratings au même endroit, hiérarchisés.
          </p>
        </header>

        <nav className="flex gap-2 mb-6 border-b border-ink-700 overflow-x-auto">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 py-2 text-body-sm font-mono uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors ${
                  active
                    ? 'border-acid-500 text-paper-50'
                    : 'border-transparent text-ink-300 hover:text-paper-50'
                }`}
                aria-current={active ? 'page' : undefined}
              >
                {t.label}
              </button>
            );
          })}
        </nav>

        {ratingsQ.isLoading && (
          <div className="text-ink-300 font-mono text-body-sm py-12 text-center">
            Chargement des ratings…
          </div>
        )}

        {ratingsQ.isError && (
          <div
            role="alert"
            className="rounded-lg border border-coral-500/30 bg-coral-500/10 px-4 py-3 text-coral-500 text-body-sm"
          >
            Impossible de charger les ratings. Réessaye dans un instant.
          </div>
        )}

        {ratingsQ.data && (
          <>
            {/* La Vue globale gère ses propres ConsistencyAlerts ; on n'affiche
                le bandeau global qu'en dehors d'`overview` pour éviter le doublon. */}
            {tab !== 'overview' && (
              <ConsistencyAlerts alerts={ratingsQ.data.consistency_check ?? []} />
            )}

            {tab === 'overview' && (
              <div className="flex flex-col gap-8">
                <VueGlobalePanel snapshot={ratingsQ.data} />

                <section className="flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h2 className="text-heading-2 text-paper-50 m-0">Courbes ELO</h2>
                  </div>
                  <EloHistoryChart
                    defaultCadence={overviewCadence}
                    cadenceOptions={['classique', 'rapide', 'blitz']}
                  />
                  <ProgressionStats cadence={overviewCadence} />
                  <div className="flex gap-1" role="group" aria-label="Cadence progression">
                    {(['classique', 'rapide', 'blitz'] as CadenceTab[]).map((c) => {
                      const active = c === overviewCadence;
                      return (
                        <button
                          type="button"
                          key={c}
                          onClick={() => setOverviewCadence(c)}
                          className={`px-2.5 py-1 text-body-sm font-mono uppercase tracking-wider rounded-md border transition-colors ${
                            active
                              ? 'border-acid-500 text-acid-500 bg-ink-900'
                              : 'border-ink-700 text-ink-300 hover:text-paper-50'
                          }`}
                          aria-pressed={active}
                        >
                          {c === 'classique' ? 'Classique' : c === 'rapide' ? 'Rapide' : 'Blitz'}
                        </button>
                      );
                    })}
                  </div>
                </section>
              </div>
            )}

            {tab === 'fide' && <FideTab />}

            {tab === 'lichess' && <LichessTab />}

            {tab === 'chesscom' && <ChesscomTab />}

            {tab === 'performance' && <PerformancePage />}
          </>
        )}
      </main>
    </>
  );
}
